export type PeriodMode = 'date' | 'month';

export interface PeriodRange {
  from: string;
  to: string;
}

export interface PeriodPreset extends PeriodRange {
  id: string;
  label: string;
  description: string;
}

export interface CalendarDayCell {
  value: string;
  day: number;
}

const DAY_MS = 86_400_000;

function dateParts(value: string): [number, number, number] {
  const [year = 1970, month = 1, day = 1] = value.split('-').map(Number);
  return [year, month, day];
}

function isoDate(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function utcDate(value: string): Date {
  const [year, month, day] = dateParts(value);
  return new Date(Date.UTC(year, month - 1, day));
}

function clamp(value: string, min: string, max: string): string {
  return value < min ? min : value > max ? max : value;
}

export function addDays(value: string, amount: number): string {
  const date = utcDate(value);
  date.setUTCDate(date.getUTCDate() + amount);
  return isoDate(date);
}

export function addMonths(value: string, amount: number): string {
  const [year, month] = dateParts(`${value.slice(0, 7)}-01`);
  const date = new Date(Date.UTC(year, month - 1 + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function addMonthsToDate(value: string, amount: number): string {
  const [year, month, day] = dateParts(value);
  const target = new Date(Date.UTC(year, month - 1 + amount, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return isoDate(target);
}

export function periodLength(mode: PeriodMode, range: PeriodRange): number {
  if (mode === 'month') {
    const [fromYear, fromMonth] = dateParts(`${range.from}-01`);
    const [toYear, toMonth] = dateParts(`${range.to}-01`);
    return (toYear - fromYear) * 12 + toMonth - fromMonth + 1;
  }
  return Math.round((utcDate(range.to).getTime() - utcDate(range.from).getTime()) / DAY_MS) + 1;
}

export function shiftPeriod(
  mode: PeriodMode,
  range: PeriodRange,
  direction: -1 | 1,
  min: string,
  max: string,
): PeriodRange {
  const length = periodLength(mode, range);
  if (mode === 'month') {
    let from = addMonths(range.from, direction * length);
    let to = addMonths(range.to, direction * length);
    if (from < min) {
      from = min;
      to = clamp(addMonths(min, length - 1), min, max);
    } else if (to > max) {
      to = max;
      from = clamp(addMonths(max, -(length - 1)), min, max);
    }
    return { from, to };
  }

  let from = addDays(range.from, direction * length);
  let to = addDays(range.to, direction * length);
  if (from < min) {
    from = min;
    to = clamp(addDays(min, length - 1), min, max);
  } else if (to > max) {
    to = max;
    from = clamp(addDays(max, -(length - 1)), min, max);
  }
  return { from, to };
}

function preset(
  id: string,
  label: string,
  description: string,
  from: string,
  to: string,
  min: string,
  max: string,
): PeriodPreset {
  return { id, label, description, from: clamp(from, min, max), to: clamp(to, min, max) };
}

export function periodPresets(mode: PeriodMode, min: string, max: string): PeriodPreset[] {
  if (mode === 'month') {
    const year = max.slice(0, 4);
    return [
      preset('all', 'Tutto', 'Intera cronologia', min, max, min, max),
      preset('current-month', 'Mese corrente', 'Ultimo mese disponibile', max, max, min, max),
      preset('last-3', 'Ultimi 3 mesi', 'Trimestre mobile', addMonths(max, -2), max, min, max),
      preset('last-6', 'Ultimi 6 mesi', 'Semestre mobile', addMonths(max, -5), max, min, max),
      preset('last-12', 'Ultimi 12 mesi', 'Anno mobile', addMonths(max, -11), max, min, max),
      preset('year', `Anno ${year}`, 'Da gennaio all’ultimo dato', `${year}-01`, max, min, max),
    ];
  }

  const monthStart = `${max.slice(0, 7)}-01`;
  const year = max.slice(0, 4);
  return [
    preset('all', 'Tutto', 'Intera cronologia', min, max, min, max),
    preset(
      'current-month',
      'Mese corrente',
      'Dal primo giorno del mese',
      monthStart,
      max,
      min,
      max,
    ),
    preset('last-30', 'Ultimi 30 giorni', 'Finestra giornaliera', addDays(max, -29), max, min, max),
    preset('last-90', 'Ultimi 90 giorni', 'Finestra trimestrale', addDays(max, -89), max, min, max),
    preset(
      'last-6-months',
      'Ultimi 6 mesi',
      'Finestra semestrale',
      addDays(addMonthsToDate(max, -6), 1),
      max,
      min,
      max,
    ),
    preset('year', `Anno ${year}`, 'Da gennaio all’ultimo dato', `${year}-01-01`, max, min, max),
  ];
}

export function calendarMonth(value: string): {
  leadingEmptyDays: number;
  days: CalendarDayCell[];
} {
  const [year, month] = dateParts(`${value}-01`);
  const firstDay = new Date(Date.UTC(year, month - 1, 1));
  const dayCount = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    leadingEmptyDays: (firstDay.getUTCDay() + 6) % 7,
    days: Array.from({ length: dayCount }, (_, index) => ({
      day: index + 1,
      value: `${value}-${String(index + 1).padStart(2, '0')}`,
    })),
  };
}
